/**
 * CommitPipeline — orchestrates workspace mutations.
 *
 * Uses commitSigning.ts for server-valid commit building.
 * Does NOT know about commit format details (kid derivation, signature).
 */

import { packFileContent, resolveContentBytes, resolveContentRange, resolveContentStream, resolveContentStreamRange } from './codec/content'
import { resolveCompression, getDecompressor } from './codec/compress'
import { encryptState, buildAad, type WorkspaceState, type WriterStateEntry } from './codec/state'
import { buildTreeFromPaths, mutateEntry, type DirEntryFile, type DirEntry, type DirEntryDir, type DirEntrySymlink, type DirObjectStore } from './codec/dirTree'
import type { ContentRef, CryptoProvider, SyncScheduler } from './types'
import { EngineException } from './types'
import { BlobRepository } from './blobRepository'
import { buildSignedCommit, deriveWriterKidFromSeed, type WriterEntry } from './commitSigning'

// ── Types ─────────────────────────────────────────────────────────

export interface MutableFileEntry {
    path: string
    kind: 'file' | 'directory' | 'symlink'
    mime: string
    size: number
    mtime: number
    ctime: number
    /** Existing Merkle child directory hash, present for lazy materialized directories. */
    dirHash?: string
    lastWriterKid?: string
    target?: string            // symlink target (only when kind === 'symlink')
    content: ContentRef
    meta?: Record<string, unknown>
}

/** Mutation operation for incremental commits. */
export type MutationOp = { type: 'upsert'; entry: DirEntry } | { type: 'delete' }

export interface CommitPipelineDeps {
    blobId: string
    writerSeed?: Uint8Array
    repository: BlobRepository
    crypto: CryptoProvider
    scheduler: SyncScheduler
    stateKey: Uint8Array
    fileKey: Uint8Array
}

// ── Pipeline ──────────────────────────────────────────────────────

export class CommitPipeline {
    /** Cached derived writer kid */
    private _derivedKid: string | undefined
    /** Immutable manifest metadata cache; avoids repeat reads during CAS retry/merge/ref collection. */
    private readonly manifestChunkCache = new Map<string, Array<{ hash: string; plainSize: number }>>()

    constructor(private readonly deps: CommitPipelineDeps) { }

    /**
     * Get the derived writer kid for this pipeline's writer seed.
     * Computes lazily and caches. Returns undefined if read-only.
     */
    async getWriterKid(): Promise<string | undefined> {
        if (this._derivedKid) return this._derivedKid
        if (!this.deps.writerSeed) return undefined
        this._derivedKid = await deriveWriterKidFromSeed(this.deps.writerSeed, this.deps.crypto)
        return this._derivedKid
    }

    /**
     * Get the raw writer seed for manual commit building (e.g. tombstone).
     * Returns undefined if read-only.
     */
    getWriterSeed(): Uint8Array | undefined {
        return this.deps.writerSeed
    }

    /**
     * §7.2.2 Local-first commit: creates a commit using the explicit parentHead
     * from the caller. Does NOT call getHeads() or perform CAS — stores locally
     * in IDB + outbox. syncNow() handles server push and CAS.
     */
    async commit(
        files: MutableFileEntry[],
        parentHead: string | null,
        additionalParents?: string[],
        writerStateEntries?: WriterStateEntry[],
        knownWriters?: WriterEntry[],
    ): Promise<{ commitHash: string; parents: string[]; stateHash: string; rootDirHash: string; commitBytes: Uint8Array; stateBytes: Uint8Array }> {
        if (!this.deps.writerSeed) {
            throw new EngineException({
                code: 'FORBIDDEN',
                message: 'Cannot commit: missing writer seed (read-only access)',
                retryable: false,
            })
        }

        // Build Merkle directory tree from flat file/directory/symlink list
        const treeEntries: Array<{
            path: string
            entry: Omit<DirEntryFile, 'name'> | Omit<DirEntryDir, 'name'>
        }> = []

        for (const f of files) {
            if (f.kind === 'file') {
                treeEntries.push({
                    path: f.path,
                    entry: {
                        kind: 'file' as const,
                        id: undefined,
                        mime: f.mime,
                        size: f.size,
                        mtime: f.mtime,
                        ctime: f.ctime,
                        content: f.content,
                        lastWriterKid: f.lastWriterKid,
                        meta: f.meta,
                    } satisfies Omit<DirEntryFile, 'name'>,
                })
            } else if (f.kind === 'directory') {
                treeEntries.push({
                    path: f.path,
                    entry: {
                        kind: 'directory' as const,
                        mtime: f.mtime,
                        ctime: f.ctime,
                        dirHash: f.dirHash ?? '',
                        meta: f.meta,
                    } satisfies Omit<DirEntryDir, 'name'>,
                })
            } else if (f.kind === 'symlink') {
                treeEntries.push({
                    path: f.path,
                    entry: {
                        kind: 'file' as const,
                        id: undefined,
                        mime: '',
                        size: 0,
                        mtime: f.mtime,
                        ctime: f.ctime,
                        content: { type: 'inline' as const, hash: '' },
                        lastWriterKid: f.lastWriterKid,
                        meta: f.meta,
                    } satisfies Omit<DirEntryFile, 'name'>,
                })
            }
        }

        const dirTreeDeps = {
            crypto: this.deps.crypto,
            stateKey: this.deps.stateKey,
            blobId: this.deps.blobId,
        }
        const { rootDirHash, objects: dirObjects } = await buildTreeFromPaths(treeEntries, dirTreeDeps)

        const state: WorkspaceState = {
            type: 'secureblob-state-v3',
            rootDirHash,
            ...(writerStateEntries?.length ? { writers: writerStateEntries } : {}),
        }

        const { ciphertext: stateBytes, hash: stateHash } = await encryptState(state, {
            crypto: this.deps.crypto,
            stateKey: this.deps.stateKey,
            blobId: this.deps.blobId,
        })

        // Start transaction — store state + all directory objects
        const tx = this.deps.repository.transaction(this.deps.blobId)
        tx.put(stateHash, stateBytes)
        for (const [hash, bytes] of dirObjects) {
            tx.put(hash, bytes)
        }

        // §5.3: Use explicit parentHead — no getHeads() call
        const parents = parentHead ? [parentHead] : []
        if (additionalParents) {
            for (const p of additionalParents) {
                if (!parents.includes(p)) parents.push(p)
            }
        }

        // Build signed commit via shared module (§5.4 writer preservation)
        const { commitBytes, commitHash } = await buildSignedCommit({
            blobId: this.deps.blobId,
            parents,
            stateHash,
            writerSeed: this.deps.writerSeed,
            crypto: this.deps.crypto,
            additionalWriters: knownWriters,
        })

        tx.put(commitHash, commitBytes)

        // §7.2.2: Local-only commit — store in IDB + outbox, no CAS.
        // The workspace's commitAndEmit() is responsible for triggering
        // syncNow() via triggerSync() after a successful commit.
        await tx.commit()

        return { commitHash, parents, stateHash, rootDirHash, commitBytes, stateBytes }
    }

    /**
     * §7.2.3 Incremental commit: applies targeted mutations to the existing Merkle tree
     * instead of rebuilding from scratch. O(k·log n) where k = mutations, n = total files.
     *
     * Uses dirTree.mutateEntry() for each mutation, then builds state + signed commit.
     * Falls back to commit() for bulk operations (import, migration).
     */
    async commitIncremental(
        currentRootDirHash: string | null,
        mutations: Array<{ path: string; op: MutationOp }>,
        parentHead: string | null,
        store: DirObjectStore,
        additionalParents?: string[],
        writerStateEntries?: WriterStateEntry[],
        knownWriters?: WriterEntry[],
    ): Promise<{ commitHash: string; parents: string[]; stateHash: string; rootDirHash: string; commitBytes: Uint8Array; stateBytes: Uint8Array }> {
        if (!this.deps.writerSeed) {
            throw new EngineException({
                code: 'FORBIDDEN',
                message: 'Cannot commit: missing writer seed (read-only access)',
                retryable: false,
            })
        }

        const dirTreeDeps = {
            crypto: this.deps.crypto,
            stateKey: this.deps.stateKey,
            blobId: this.deps.blobId,
        }

        // Apply each mutation sequentially (each depends on previous rootDirHash)
        let rootDirHash = currentRootDirHash
        const allDirObjects = new Map<string, Uint8Array>()

        for (const { path, op } of mutations) {
            const result = await mutateEntry(path, rootDirHash, store, dirTreeDeps, op)
            rootDirHash = result.rootDirHash
            for (const [hash, bytes] of result.objects) {
                allDirObjects.set(hash, bytes)
                // Also put into store so next mutation can read intermediate dirs
                await store.put(hash, bytes)
            }
        }

        const state: WorkspaceState = {
            type: 'secureblob-state-v3',
            rootDirHash,
            ...(writerStateEntries?.length ? { writers: writerStateEntries } : {}),
        }

        const { ciphertext: stateBytes, hash: stateHash } = await encryptState(state, {
            crypto: this.deps.crypto,
            stateKey: this.deps.stateKey,
            blobId: this.deps.blobId,
        })

        const tx = this.deps.repository.transaction(this.deps.blobId)
        tx.put(stateHash, stateBytes)
        for (const [hash, bytes] of allDirObjects) {
            tx.put(hash, bytes)
        }

        const parents = parentHead ? [parentHead] : []
        if (additionalParents) {
            for (const p of additionalParents) {
                if (!parents.includes(p)) parents.push(p)
            }
        }

        const { commitBytes, commitHash } = await buildSignedCommit({
            blobId: this.deps.blobId,
            parents,
            stateHash,
            writerSeed: this.deps.writerSeed,
            crypto: this.deps.crypto,
            additionalWriters: knownWriters,
        })

        tx.put(commitHash, commitBytes)
        await tx.commit()

        return { commitHash, parents, stateHash, rootDirHash: rootDirHash!, commitBytes, stateBytes }
    }

    /** Create a repository transaction for the pipeline's blobId. */
    createTransaction() {
        return this.deps.repository.transaction(this.deps.blobId)
    }

    async syncNow(): Promise<void> {
        await this.deps.repository.push(this.deps.blobId)
    }

    /**
     * Encrypt content and return objects WITHOUT writing to IDB.
     * v3 pipeline: CDC → compress → encryptDeterministic → dedup.
     */
    async encodeContentNoCommit(plaintext: Uint8Array, opts?: { mime?: string; path?: string }): Promise<{
        content: ContentRef; size: number;
        objects: Array<{ hash: string; bytes: Uint8Array }>
    }> {
        // §97: .mdr extension → block-radix chunking with 128-byte blocks
        const packOpts: { mime?: string; chunking?: string; blockSize?: number } = { mime: opts?.mime }
        if (opts?.path?.endsWith('.mdr')) {
            packOpts.chunking = 'block-radix'
            packOpts.blockSize = 128
        }
        const packed = await packFileContent(plaintext, this.contentCodecDeps(), packOpts)
        return {
            content: packed.content,
            size: plaintext.byteLength,
            objects: packed.objects,
        }
    }

    async encodeContent(plaintext: Uint8Array, opts?: { path?: string }): Promise<{ content: ContentRef; size: number }> {
        const result = await this.encodeContentNoCommit(plaintext, opts)
        const tx = this.deps.repository.transaction(this.deps.blobId)
        for (const obj of result.objects) tx.put(obj.hash, obj.bytes)
        await tx.commit()
        return { content: result.content, size: result.size }
    }

    /** Build full ContentCodecDeps — shared by all read/write methods. */
    private contentCodecDeps() {
        const fileAad = buildAad(this.deps.blobId, 'file')
        return {
            encryptRawObject: async (bytes: Uint8Array) => this.deps.crypto.encrypt(this.deps.fileKey, bytes, fileAad),
            decryptRawObject: async (bytes: Uint8Array) => this.deps.crypto.decrypt(this.deps.fileKey, bytes, fileAad),
            sha256: (bytes: Uint8Array) => this.deps.crypto.sha256(bytes),
            encryptDeterministic: async (bytes: Uint8Array) => this.deps.crypto.encryptDeterministic(this.deps.fileKey, bytes, fileAad),
            hasObject: async (hash: string) => this.deps.repository.hasObjectLocal(hash),
        }
    }

    async readContent(content: ContentRef): Promise<Uint8Array | null> {
        return resolveContentBytes(content, this.deps.repository, this.contentCodecDeps())
    }

    /**
     * Read a byte range from content without downloading everything.
     * v3: only fetches chunks that overlap [offset, offset+length).
     */
    async readContentRange(content: ContentRef, offset: number, length: number): Promise<Uint8Array | null> {
        return resolveContentRange(content, offset, length, this.deps.repository, this.contentCodecDeps())
    }

    /**
     * Stream content chunk-by-chunk (decompressed).
     * Each yield produces one decrypted + decompressed chunk — minimal RAM usage.
     */
    async *streamContent(content: ContentRef): AsyncGenerator<Uint8Array> {
        yield* resolveContentStream(content, this.deps.repository, this.contentCodecDeps())
    }

    /**
     * Stream content from an arbitrary byte offset with optional length limit.
     * V3: uses per-chunk plainSize for O(1) seek — skips earlier chunks entirely.
     *
     * Use cases: FUSE read() with offset, HTTP Range, seekable media, resumable downloads.
     */
    async *streamContentRange(
        content: ContentRef,
        offset: number,
        length?: number,
    ): AsyncGenerator<Uint8Array> {
        yield* resolveContentStreamRange(content, offset, length, this.deps.repository, this.contentCodecDeps())
    }

    /**
     * Read chunk refs from a v3 manifest.
     * Returns null for inline content (no manifest).
     */
    async readManifestChunks(content: ContentRef): Promise<Array<{ hash: string; plainSize: number }> | null> {
        if (content.type !== 'chunked') return null
        const cached = this.manifestChunkCache.get(content.manifestHash)
        if (cached !== undefined) return cached

        const manifestObject = await this.deps.repository.load(content.manifestHash)
        if (!manifestObject) return null
        const manifestBytes = await this.contentCodecDeps().decryptRawObject(manifestObject)
        const manifest = JSON.parse(new TextDecoder().decode(manifestBytes))
        if (manifest.chunking === 'block-radix' && Array.isArray(manifest.children)) {
            const deps = this.contentCodecDeps()
            const decompressor = getDecompressor(manifest.compression ?? 'deflate-raw')
            const chunks: Array<{ hash: string; plainSize: number }> = []
            for (const hash of manifest.children) {
                if (typeof hash !== 'string' || hash.length === 0) continue
                const encrypted = await this.deps.repository.load(hash)
                if (!encrypted) return null
                const decrypted = await deps.decryptRawObject(encrypted)
                chunks.push({ hash, plainSize: decompressor.decompress(decrypted).length })
            }
            this.manifestChunkCache.set(content.manifestHash, chunks)
            return chunks
        }
        if (!manifest.chunks || !Array.isArray(manifest.chunks)) return null
        const chunks = manifest.chunks.map((c: { hash: string; plainSize: number }) => ({
            hash: c.hash,
            plainSize: c.plainSize,
        }))
        this.manifestChunkCache.set(content.manifestHash, chunks)
        return chunks
    }

    // ── SQLite Page-Aligned Encoding (§94 §4.2) ────────────────────

    /**
     * Encode dirty pages as page-aligned CAS objects.
     * Creates a manifest with `chunking: 'sqlite-page-aligned'` and
     * per-page `pgno` metadata in each chunk entry.
     */
    async encodeDbPages(
        dirtyPages: Map<number, Uint8Array>,
        pageSize: number,
    ): Promise<{ content: ContentRef; size: number }> {
        const deps = this.contentCodecDeps()
        const tx = this.deps.repository.transaction(this.deps.blobId)
        const chunks: Array<{ hash: string; plainSize: number; pgno: number }> = []

        // Sort by pgno for deterministic ordering
        const sorted = [...dirtyPages.entries()].sort((a, b) => a[0] - b[0])

        const compressor = resolveCompression({ compression: 'deflate-raw' })

        for (const [pgno, pageData] of sorted) {
            const compressed = compressor.compress(pageData)
            const encrypted = await deps.encryptDeterministic(compressed)
            const hash = await deps.sha256(encrypted)
            tx.put(hash, encrypted)
            chunks.push({ hash, plainSize: pageData.length, pgno })
        }

        // Build page-aligned manifest
        const manifest = {
            type: 'secureblob-file-manifest',
            v: 3,
            chunking: 'sqlite-page-aligned',
            compression: 'deflate-raw',
            params: { pageSize },
            totalPlainSize: dirtyPages.size * pageSize,
            plaintextSha256: null,
            chunks,
        }

        const manifestBytes = new TextEncoder().encode(JSON.stringify(manifest))
        const manifestEncrypted = await deps.encryptDeterministic(manifestBytes)
        const manifestHash = await deps.sha256(manifestEncrypted)
        tx.put(manifestHash, manifestEncrypted)
        await tx.commit()

        return {
            content: { type: 'chunked' as const, manifestHash },
            size: dirtyPages.size * pageSize,
        }
    }

    /**
     * Read sqlite pages from a page-aligned manifest.
     * Returns Map<pgno, pageData> or null if not a page-aligned manifest.
     */
    async readSqlitePages(content: ContentRef): Promise<Map<number, Uint8Array> | null> {
        if (content.type === 'inline') {
            // Inline content: read full bytes and split into pages
            const fullBytes = await this.readContent(content)
            if (!fullBytes || fullBytes.length === 0) return null
            // Detect page size from DB header (bytes 16-17)
            let ps = fullBytes.length >= 18 ? (fullBytes[16] << 8) | fullBytes[17] : 4096
            if (ps === 0) ps = 4096
            if (ps === 1) ps = 65536
            const pages = new Map<number, Uint8Array>()
            for (let i = 0; i < fullBytes.length; i += ps) {
                pages.set(Math.floor(i / ps) + 1, fullBytes.slice(i, i + ps))
            }
            return pages
        }

        if (content.type !== 'chunked') return null

        const manifestObject = await this.deps.repository.load(content.manifestHash)
        if (!manifestObject) return null
        const manifestBytes = await this.contentCodecDeps().decryptRawObject(manifestObject)
        const manifest = JSON.parse(new TextDecoder().decode(manifestBytes))

        // Only handle page-aligned manifests
        if (manifest.chunking !== 'sqlite-page-aligned') {
            // Fallback: read full content and split into pages
            const fullBytes = await this.readContent(content)
            if (!fullBytes) return null
            const ps = manifest.params?.pageSize ?? 4096
            const pages = new Map<number, Uint8Array>()
            for (let i = 0; i < fullBytes.length; i += ps) {
                pages.set(Math.floor(i / ps) + 1, fullBytes.slice(i, i + ps))
            }
            return pages
        }

        // Page-aligned: each chunk has a pgno
        const deps = this.contentCodecDeps()
        const pages = new Map<number, Uint8Array>()
        const decompressor = getDecompressor(manifest.compression ?? 'deflate-raw')
        for (const chunk of manifest.chunks) {
            const encrypted = await this.deps.repository.load(chunk.hash)
            if (!encrypted) {
                throw new Error(`Missing sqlite page object: pgno=${chunk.pgno} hash=${chunk.hash}`)
            }
            const decrypted = await deps.decryptRawObject(encrypted)
            const pageData = decompressor.decompress(decrypted)
            if (pageData.length !== chunk.plainSize) {
                throw new Error(`Corrupted sqlite page object: pgno=${chunk.pgno} plainSize (${chunk.plainSize}) !== actual (${pageData.length})`)
            }
            pages.set(chunk.pgno, pageData)
        }
        return pages
    }

    /**
     * Rebuild a v3 manifest from merged chunk refs.
     * Preserves chunking/compression params from the base manifest.
     * Sets `plaintextSha256 = null` since whole-file hash can't be recomputed without re-reading all chunks.
     */
    async rebuildManifest(
        chunks: Array<{ hash: string; plainSize: number }>,
        baseContent: ContentRef,
    ): Promise<{ content: ContentRef; totalPlainSize: number }> {
        if (baseContent.type !== 'chunked') {
            throw new Error('rebuildManifest: baseContent must be chunked')
        }

        // Read base manifest to preserve params
        const baseManifestObj = await this.deps.repository.load(baseContent.manifestHash)
        if (!baseManifestObj) throw new Error('rebuildManifest: could not fetch base manifest')
        const baseDecrypted = await this.contentCodecDeps().decryptRawObject(baseManifestObj)
        const baseManifest = JSON.parse(new TextDecoder().decode(baseDecrypted))

        const totalPlainSize = chunks.reduce((sum, c) => sum + c.plainSize, 0)
        let mergedManifest: Record<string, unknown>
        if (baseManifest.chunking === 'block-radix') {
            const fanout = typeof baseManifest.params?.fanout === 'number' ? baseManifest.params.fanout : 256
            const children: Array<string | null> = []
            const chunkHashes = chunks.map(c => c.hash)
            let cursor = 0
            if (Array.isArray(baseManifest.children)) {
                for (const child of baseManifest.children) {
                    if (child === null) {
                        children.push(null)
                    } else if (typeof child === 'string') {
                        children.push(chunkHashes[cursor++] ?? null)
                    }
                }
            }
            while (cursor < chunkHashes.length) {
                children.push(chunkHashes[cursor++])
            }

            const blockCount = children.length
            const depth = blockCount <= 1 ? 1 : Math.ceil(Math.log(blockCount) / Math.log(fanout))
            mergedManifest = {
                type: 'secureblob-file-manifest',
                v: 3,
                chunking: 'block-radix',
                compression: baseManifest.compression || 'deflate-raw',
                params: {
                    ...(baseManifest.params ?? {}),
                    blockCount,
                    depth,
                    fanout,
                },
                children,
            }
        } else {
            mergedManifest = {
                type: 'secureblob-file-manifest',
                v: 3,
                chunking: baseManifest.chunking || 'fastcdc-gear',
                compression: baseManifest.compression || 'deflate-raw',
                params: baseManifest.params,
                totalPlainSize,
                plaintextSha256: null,
                chunks: chunks.map(c => ({ hash: c.hash, plainSize: c.plainSize })),
            }
        }

        const deps = this.contentCodecDeps()
        const manifestBytes = new TextEncoder().encode(JSON.stringify(mergedManifest))
        const encrypted = await deps.encryptDeterministic(manifestBytes)
        const hash = await deps.sha256(encrypted)

        const tx = this.deps.repository.transaction(this.deps.blobId)
        tx.put(hash, encrypted)
        await tx.commit()

        return {
            content: { type: 'chunked' as const, manifestHash: hash },
            totalPlainSize,
        }
    }

    // ── TB-Scale SQLite Delta I/O (§94 §3) ─────────────────────────

    /**
     * Read only manifest metadata (pgno→hash) without loading any page data.
     * O(1) manifest read — foundation for manifest-level diff.
     */
    async readSqliteManifest(content: ContentRef): Promise<Map<number, string> | null> {
        if (content.type !== 'chunked') return null
        const obj = await this.deps.repository.load(content.manifestHash)
        if (!obj) return null
        const manifest = JSON.parse(new TextDecoder().decode(
            await this.contentCodecDeps().decryptRawObject(obj)))
        if (manifest.chunking !== 'sqlite-page-aligned') return null
        const map = new Map<number, string>()
        for (const c of manifest.chunks) map.set(c.pgno, c.hash)
        return map
    }

    /**
     * Read ONLY the requested pages by pgno from a page-aligned manifest.
     * O(k) where k = requested pages. Uses loadBatch for efficiency.
     */
    async readSqlitePagesByPgno(
        content: ContentRef,
        pgnos: Set<number>,
    ): Promise<Map<number, Uint8Array> | null> {
        if (content.type !== 'chunked') return null
        const obj = await this.deps.repository.load(content.manifestHash)
        if (!obj) return null
        const manifest = JSON.parse(new TextDecoder().decode(
            await this.contentCodecDeps().decryptRawObject(obj)))
        if (manifest.chunking !== 'sqlite-page-aligned') return null

        // Collect only needed chunk hashes
        const needed: Array<{ pgno: number; hash: string; plainSize: number }> = []
        for (const c of manifest.chunks) {
            if (pgnos.has(c.pgno)) needed.push({ pgno: c.pgno, hash: c.hash, plainSize: c.plainSize })
        }
        if (needed.length === 0) return new Map()

        // Batch load from CAS — single round-trip
        const batchData = await this.deps.repository.loadBatch(needed.map(n => n.hash))

        const deps = this.contentCodecDeps()
        const decompressor = getDecompressor(manifest.compression ?? 'deflate-raw')
        const pages = new Map<number, Uint8Array>()
        for (const { pgno, hash, plainSize } of needed) {
            const enc = batchData.get(hash)
            if (!enc) {
                throw new Error(`Missing sqlite page object: pgno=${pgno} hash=${hash}`)
            }
            const pageData = decompressor.decompress(await deps.decryptRawObject(enc))
            if (pageData.length !== plainSize) {
                throw new Error(`Corrupted sqlite page object: pgno=${pgno} plainSize (${plainSize}) !== actual (${pageData.length})`)
            }
            pages.set(pgno, pageData)
        }
        return pages
    }

    /**
     * Delta-encode: reference base manifest CAS hashes, only encrypt CHANGED pages.
     * O(k) encryption where k = changed pages. Unchanged pages → 0 bytes, 0 encryption.
     * Leverages v3 deterministic encryption for CAS dedup.
     */
    async encodeSqliteDeltaPages(
        changedPages: Map<number, Uint8Array>,
        baseContent: ContentRef,
        pageSize: number,
        nTruncate?: number,
    ): Promise<{ content: ContentRef; size: number }> {
        const baseMeta = await this.readSqliteManifest(baseContent)
        if (!baseMeta) {
            // Fallback: full encode if base has no page-aligned manifest
            return this.encodeDbPages(changedPages, pageSize)
        }

        const deps = this.contentCodecDeps()
        const tx = this.deps.repository.transaction(this.deps.blobId)
        const chunks: Array<{ hash: string; plainSize: number; pgno: number }> = []
        const compressor = resolveCompression({ compression: 'deflate-raw' })

        // Union of all pgnos (base + changed), sorted for deterministic manifest.
        // Filter: if nTruncate > 0 (DB shrunk, e.g. VACUUM), exclude pages beyond nTruncate.
        const maxPgno = nTruncate && nTruncate > 0 ? nTruncate : Infinity
        const allPgnos = [...new Set([...baseMeta.keys(), ...changedPages.keys()])]
            .filter(pgno => pgno <= maxPgno)
            .sort((a, b) => a - b)
        for (const pgno of allPgnos) {
            if (changedPages.has(pgno)) {
                // Changed page: encrypt + store new CAS object
                const pageData = changedPages.get(pgno)!
                const compressed = compressor.compress(pageData)
                const encrypted = await deps.encryptDeterministic(compressed)
                const hash = await deps.sha256(encrypted)
                tx.put(hash, encrypted)  // CAS dedup: no-op if hash exists
                chunks.push({ hash, plainSize: pageData.length, pgno })
            } else {
                // Unchanged page: reference existing base hash — 0 bytes, 0 encryption
                chunks.push({ hash: baseMeta.get(pgno)!, plainSize: pageSize, pgno })
            }
        }

        // New manifest referencing both existing + new CAS objects
        const manifest = {
            type: 'secureblob-file-manifest',
            v: 3,
            chunking: 'sqlite-page-aligned',
            compression: 'deflate-raw',
            params: { pageSize },
            totalPlainSize: chunks.length * pageSize,
            plaintextSha256: null,
            chunks,
        }

        const manifestBytes = new TextEncoder().encode(JSON.stringify(manifest))
        const manifestEncrypted = await deps.encryptDeterministic(manifestBytes)
        const manifestHash = await deps.sha256(manifestEncrypted)
        tx.put(manifestHash, manifestEncrypted)
        await tx.commit()

        return {
            content: { type: 'chunked' as const, manifestHash },
            size: chunks.length * pageSize,
        }
    }
}
